import { useState } from "react";
import { toast } from "sonner";
import { Paperclip, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_EXT = [".png", ".jpg", ".jpeg", ".webp", ".gif", ".pdf", ".ai", ".psd", ".heic", ".heif"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFileChange = (e) => {
    const incoming = Array.from(e.target.files || []);
    const merged = [...files, ...incoming];
    if (merged.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files. Trimming extras.`);
    }
    const trimmed = merged.slice(0, MAX_FILES).filter((f) => {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(`${f.name}: unsupported file type`);
        return false;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: too large (max 10 MB)`);
        return false;
      }
      return true;
    });
    setFiles(trimmed);
    e.target.value = "";
  };

  const removeFile = (idx) => {
    setFiles((arr) => arr.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await api.post("/contact", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
      setFiles([]);
      toast.success("Message sent. We'll get back to you within 24h.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page" className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left: huge text */}
        <div className="lg:col-span-6">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-6">
            Contact
          </p>
          <h1 className="font-display font-black text-7xl md:text-9xl leading-[0.82] tracking-tighter">
            Let's
            <br />
            <span className="text-[#E6007A]">print.</span>
          </h1>
          <div className="mt-12 space-y-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-1">
                Email
              </p>
              <a
                href="mailto:Shopxemi@gmail.com"
                data-testid="contact-info-email"
                className="font-display font-black text-2xl hover:text-[#E6007A] transition-colors"
              >
                Shopxemi@gmail.com
              </a>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-1">
                Phone
              </p>
              <a
                href="tel:07828274315"
                data-testid="contact-info-phone"
                className="font-display font-black text-2xl hover:text-[#00E5FF] transition-colors"
              >
                07828 274315
              </a>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-1">
                Hours
              </p>
              <p className="font-display font-black text-2xl">Mon–Fri · 09—18</p>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-6">
          {done ? (
            <div
              data-testid="contact-success"
              className="border border-black bg-white p-10"
            >
              <div className="w-12 h-12 bg-[#FFD600] mb-6" />
              <h2 className="font-display font-black text-4xl mb-4">Got it.</h2>
              <p className="font-mono text-sm text-[#27272A] leading-relaxed mb-8">
                Your message is in our inbox. Expect a reply within 24 hours, often much faster.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="contact-send-another"
                className="btn-ink bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-[0.25em]"
              >
                Send another →
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-black/15 bg-white p-8 md:p-10 space-y-5"
            >
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                name="website"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label="Name"
                  value={form.name}
                  onChange={(v) => update("name", v)}
                  testid="contact-name"
                  required
                />
                <Field
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  testid="contact-email"
                  required
                />
              </div>
              <Field
                label="Subject"
                value={form.subject}
                onChange={(v) => update("subject", v)}
                testid="contact-subject"
              />
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
                  Tell us about your project *
                </label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  data-testid="contact-message"
                  onChange={(e) => update("message", e.target.value)}
                  className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-black focus:ring-2 focus:ring-black/10 rounded-none resize-none"
                  maxLength={2000}
                />
              </div>

              {/* Attachments */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
                  Attachments (optional · max {MAX_FILES} files, 10 MB each)
                </label>
                <label
                  htmlFor="contact-files"
                  data-testid="contact-files-trigger"
                  className="inline-flex items-center gap-2 border border-dashed border-black/25 hover:border-black px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] cursor-pointer transition-colors"
                >
                  <Paperclip size={14} /> Attach artwork or photos
                </label>
                <input
                  id="contact-files"
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.ai,.psd,.heic,.heif,image/*,application/pdf"
                  data-testid="contact-files-input"
                  onChange={onFileChange}
                  className="hidden"
                />
                <p className="font-mono text-[10px] text-[#71717A] mt-2">
                  PNG · JPG · WEBP · PDF · AI · PSD · HEIC
                </p>
                {files.length > 0 && (
                  <ul data-testid="contact-files-list" className="mt-3 space-y-2">
                    {files.map((f, idx) => (
                      <li
                        key={idx}
                        data-testid={`contact-file-${idx}`}
                        className="flex items-center justify-between gap-3 bg-[#F4F4F5] px-3 py-2 border border-black/10"
                      >
                        <span className="font-mono text-xs truncate">
                          <Paperclip size={12} className="inline-block mr-1.5 -mt-px" />
                          {f.name}
                          <span className="text-[#71717A]"> · {(f.size / 1024).toFixed(0)} KB</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(idx)}
                          data-testid={`contact-file-remove-${idx}`}
                          aria-label="Remove"
                          className="text-[#71717A] hover:text-[#E6007A] shrink-0"
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit"
                className="btn-ink-magenta w-full bg-black text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send message →"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-black focus:ring-2 focus:ring-black/10 rounded-none"
      />
    </div>
  );
}
